import { useEffect, useRef, useState } from 'react';
import establishmentsApi from 'api/requests/establishments';
import { STATUS_TYPES } from 'modules/_common/EstablishmentOrderStatusTypes';

const POLL_INTERVAL_MS = 10000;
// How long the "new orders" highlight (the pulsing dot) stays visible after an
// order arrives. Re-armed on each new detection.
const NEW_ORDERS_HIGHLIGHT_MS = 10000;
const NOTIFICATION_SOUND_SRC = '/assets/sounds/new_order_notification.mp3';

/**
 * Page-level detection of new orders for the PDV screen. Polls the "requested"
 * orders continuously (regardless of the selected tab) and, for genuinely new
 * orders:
 *  - plays a notification sound;
 *  - briefly raises `hasNewOrders` so a pulsing dot can flash on the tab;
 *  - bumps `newOrdersToken` so a list showing requested orders can refetch.
 */
const REQUESTED_TAB = STATUS_TYPES.REQUESTED.key;

export default function useNewOrdersNotification({
    establishmentSlug,
    eventSlug,
    activeTab,
    soundEnabled = true,
}) {
    // Whether the "new orders" dot is visible. While the user is on the
    // "Solicitado" tab it auto-hides after NEW_ORDERS_HIGHLIGHT_MS; on any other
    // tab it stays until the user opens "Solicitado".
    const [hasNewOrders, setHasNewOrders] = useState(false);
    // Bumps every time new "requested" orders are detected. Consumers use it to
    // refetch a list that should reflect them.
    const [newOrdersToken, setNewOrdersToken] = useState(0);

    const soundEnabledRef = useRef(soundEnabled);
    const audioRef = useRef(null);
    const highlightTimeoutRef = useRef(null);
    const activeTabRef = useRef(activeTab);
    // Ids of "requested" orders seen on the previous poll. `null` means "no
    // baseline yet" — the first poll only establishes it and never notifies.
    const knownRequestedIdsRef = useRef(null);

    const clearHideTimer = () => {
        if (highlightTimeoutRef.current) {
            clearTimeout(highlightTimeoutRef.current);
            highlightTimeoutRef.current = null;
        }
    };

    const startHideTimer = () => {
        clearHideTimer();
        highlightTimeoutRef.current = setTimeout(() => {
            setHasNewOrders(false);
            highlightTimeoutRef.current = null;
        }, NEW_ORDERS_HIGHLIGHT_MS);
    };

    useEffect(() => {
        soundEnabledRef.current = soundEnabled;
    }, [soundEnabled]);

    useEffect(() => {
        activeTabRef.current = activeTab;

        // Cancel any pending auto-hide. Off "Solicitado" this lets a visible dot
        // persist; opening "Solicitado" acknowledges the orders, so hide it now.
        clearHideTimer();
        if (activeTab === REQUESTED_TAB) {
            setHasNewOrders(false);
        }
    }, [activeTab]);

    useEffect(() => {
        const audio = new Audio(NOTIFICATION_SOUND_SRC);
        audio.load();
        audioRef.current = audio;

        // Browsers block programmatic audio until the user interacts with the
        // page (autoplay policy). Prime the element on the first user gesture so
        // later poll-driven plays are allowed.
        const unlock = () => {
            audio.play()
                .then(() => {
                    audio.pause();
                    audio.currentTime = 0;
                })
                .catch(() => { /* will retry on the next gesture */ });

            removeUnlockListeners();
        };

        const removeUnlockListeners = () => {
            window.removeEventListener('pointerdown', unlock);
            window.removeEventListener('keydown', unlock);
            window.removeEventListener('touchstart', unlock);
        };

        window.addEventListener('pointerdown', unlock);
        window.addEventListener('keydown', unlock);
        window.addEventListener('touchstart', unlock);

        return () => {
            removeUnlockListeners();
            audio.pause();
            audioRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (!establishmentSlug || !eventSlug) return;

        // Fresh event context: drop the baseline and any pending highlight so a
        // new selection never notifies for pre-existing orders.
        knownRequestedIdsRef.current = null;
        setHasNewOrders(false);

        let isMounted = true;
        let timeoutId;

        const playSound = () => {
            if (!audioRef.current || !soundEnabledRef.current) return;

            audioRef.current.currentTime = 0;
            audioRef.current.play().catch((error) => {
                console.error('Failed to play notification sound:', error);
            });
        };

        const poll = async () => {
            if (!isMounted) return;

            try {
                const { data } = await establishmentsApi.getEstablishmentOrders(
                    establishmentSlug,
                    eventSlug,
                    { status: STATUS_TYPES.REQUESTED.key, page: 1 }
                );

                const requestedIds = (data?.data ?? []).map((order) => order.id);
                const knownIds = knownRequestedIdsRef.current;

                if (knownIds === null) {
                    // First poll for this event — establish the baseline only.
                    knownRequestedIdsRef.current = new Set(requestedIds);
                } else {
                    const newIds = requestedIds.filter((id) => !knownIds.has(id));
                    knownRequestedIdsRef.current = new Set(requestedIds);

                    if (newIds.length > 0) {
                        playSound();
                        // Signal consumers (e.g. the orders table) to refetch.
                        setNewOrdersToken((token) => token + 1);

                        setHasNewOrders(true);
                        // On "Solicitado" the user sees the orders, so flash and
                        // fade; elsewhere keep the dot until they open the tab.
                        if (activeTabRef.current === REQUESTED_TAB) {
                            startHideTimer();
                        } else {
                            clearHideTimer();
                        }
                    }
                }
            } catch (error) {
                console.error(error);
            }

            if (isMounted) {
                timeoutId = setTimeout(poll, POLL_INTERVAL_MS);
            }
        };

        poll();

        return () => {
            isMounted = false;
            if (timeoutId) clearTimeout(timeoutId);
            if (highlightTimeoutRef.current) {
                clearTimeout(highlightTimeoutRef.current);
                highlightTimeoutRef.current = null;
            }
        };
    }, [establishmentSlug, eventSlug]);

    return { hasNewOrders, newOrdersToken };
}
